import { getAccountId } from '@seek/adv-account-selection';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { UTILS_RESET_PERSISTENT_DATA } from 'src/app/shared/constants/dispatcherId';
import {
  PAGE_ID_EDIT,
  PAGE_ID_SUCCESS,
} from 'src/app/shared/constants/pageUid';
import { QP_VALUE } from 'src/app/shared/constants/queryParams';
import {
  ROUTE_EDIT_PAY_AND_POST,
  ROUTE_EDIT_SUCCESS,
  ROUTE_PAY_AND_POST_V2,
  ROUTE_REFRESH,
  ROUTE_REFRESH_SUCCESS,
  ROUTE_SUCCESS,
} from 'src/app/shared/constants/routes';
// @ts-ignore
import { hasIdChanged } from 'src/app/utils/hasIdChanged';
import utils from 'src/app/v1/state/dispatcher/utils';
import { actionUpdateIsDataCleanupComplete } from 'src/app/v1/state/modules/app/isDataCleanupComplete';
import { usePrevious } from 'src/app/v1/views/hooks/usePrevious';
import { useQueryParams } from 'src/app/v1/views/hooks/useQueryParams';
import type { AppState } from 'src/types/appState';

interface StateProps {
  advertiserId?: string;
  draftId?: string;
  isDataCleanupComplete?: boolean;
  isJobPosted?: boolean;
  isJobPostingSuccessStepsCompleted?: boolean;
  pageId: string;
  sourceJobId?: string;
  queryParamsAdvertiserId?: string;
  queryParamsDraftId?: string;
}

export const useDataCleanup = () => {
  const {
    advertiserId,
    draftId,
    isDataCleanupComplete,
    isJobPosted,
    isJobPostingSuccessStepsCompleted,
    pageId,
    queryParamsAdvertiserId,
    queryParamsDraftId,
    sourceJobId,
  } = useSelector((state: AppState): StateProps => {
    const sessionPageId = state.session?.pageId;
    const isSuccessPage = sessionPageId === PAGE_ID_SUCCESS;
    const isNotificationsSettingCompleted =
      state.session?.notificationsSetting?.isSettingCompleted;
    const isSessionJobPosted = state.session.isJobPosted;
    const isJobPostingSuccessCompleted =
      (isSessionJobPosted && !isSuccessPage) ||
      (isSuccessPage && isNotificationsSettingCompleted);

    return {
      advertiserId: state.session.advertiserId,
      draftId: state.session.draftId,
      isDataCleanupComplete: state.app.isDataCleanupComplete,
      isJobPosted: isSessionJobPosted,
      isJobPostingSuccessStepsCompleted: isJobPostingSuccessCompleted,
      pageId: sessionPageId,
      queryParamsAdvertiserId: state.app.queryParams.advertiserid,
      queryParamsDraftId: state.app.queryParams.draftid,
      sourceJobId: state.session.sourceJobId,
    };
  }, shallowEqual);

  const [isLoading, setIsLoading] = useState(true);

  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const prevProps = usePrevious({ pageId, pathname });

  const { getJobIdParam, getReferrerParam } = useQueryParams();

  useEffect(() => {
    let hasAdvertiserIdChanged = false;
    let hasDraftIdChanged = false;
    let hasJobIdChanged = false;
    let isCreateJob = false;
    let isCopyJob = false;

    const jobIdParam = getJobIdParam();
    const referrerParam = getReferrerParam();
    const accountId = getAccountId();

    const newAdvertiserId = queryParamsAdvertiserId || accountId;
    if (newAdvertiserId) {
      hasAdvertiserIdChanged = hasIdChanged(advertiserId, newAdvertiserId);
    }

    if (queryParamsDraftId) {
      hasDraftIdChanged = hasIdChanged(draftId, queryParamsDraftId);
    }

    if (jobIdParam) {
      hasJobIdChanged = hasIdChanged(sourceJobId, jobIdParam);
    }

    if (referrerParam) {
      if (referrerParam === QP_VALUE.CREATE_JOB) {
        isCreateJob = true;
      } else if (referrerParam === QP_VALUE.COPY_JOB) {
        isCopyJob = true;
      }
    }

    // Handle stale store data reset for Edit job upgrade
    // isJobPosted store value is retained from previous create, edit upgraded job or refresh flow:
    // - create job success page > dashboard > edit page
    // - edit STO upgrade job success page > dashboard > edit page
    // - refresh job success page > dashboard > edit page
    // NOTE: using pageId over pathname here as there are multiple success pathnames
    // but they all use same pageId of success
    const isEditingPostedJob =
      isJobPosted &&
      prevProps?.pageId === undefined &&
      (pageId === PAGE_ID_SUCCESS || pageId === PAGE_ID_EDIT);

    if (
      hasAdvertiserIdChanged ||
      hasDraftIdChanged ||
      hasJobIdChanged ||
      isCreateJob ||
      isCopyJob ||
      isEditingPostedJob ||
      isJobPostingSuccessStepsCompleted
    ) {
      dispatch(utils(UTILS_RESET_PERSISTENT_DATA));
    } else {
      dispatch(actionUpdateIsDataCleanupComplete(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent hirers going from the Success Page back into Job posting flows
  // NOTE: Is this needed now that we have history.block logic in SuccessPage?
  useEffect(() => {
    if (
      (isJobPosted &&
        prevProps?.pathname === ROUTE_SUCCESS &&
        pathname === ROUTE_PAY_AND_POST_V2) ||
      (prevProps?.pathname === ROUTE_EDIT_SUCCESS &&
        pathname === ROUTE_EDIT_PAY_AND_POST) ||
      (prevProps?.pathname === ROUTE_REFRESH_SUCCESS &&
        pathname === ROUTE_REFRESH)
    ) {
      dispatch(utils(UTILS_RESET_PERSISTENT_DATA));
    }
    // Only listen for isJobPosted and pathname changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJobPosted, pathname]);

  useEffect(() => {
    if (isDataCleanupComplete) {
      setIsLoading(false);
    }
  }, [isDataCleanupComplete]);

  return { isLoading };
};
